import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { api, HOURS_OPTIONS, roleLabel } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["probation", "active", "sickness", "offboarded", "other"];

const empty = {
  full_name: "", job_role: "", rcsm_id: "", cnat_ids: [], address: "", postcode: "",
  work_phone: "", personal_phone: "", work_email: "", hours: "Full Time", region_id: "",
  employment_status: "probation", elearning_percentage: 0, kpi_compliance: 0,
  supervision_count: 0, start_date: "", next_review_date: "",
};

export default function EmployeeForm({ open, onOpenChange, employee, onSaved }) {
  const [form, setForm] = useState(empty);
  const [regions, setRegions] = useState([]);
  const [rcsms, setRcsms] = useState([]);
  const [cnats, setCnats] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [trainers, setTrainers] = useState([]);
  const [newStarter, setNewStarter] = useState(null);
  const [trainerId, setTrainerId] = useState("");
  const [allocating, setAllocating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewStarter(null); setTrainerId("");
    Promise.all([
      api.get("/regions"),
      api.get("/users", { params: { role: "rcsm" } }),
      api.get("/users", { params: { role: "cnat" } }),
      api.get("/job-roles"),
      api.get("/users", { params: { role: "clinical_training_manager" } }),
    ]).then(([r, rc, cn, jr, ctm]) => {
      setRegions(r.data); setRcsms(rc.data); setCnats(cn.data); setJobRoles(jr.data);
      setTrainers([...(ctm.data || []), ...(cn.data || [])]);
    });
    setForm(employee ? { ...empty, ...employee, cnat_ids: (employee.cnats || []).map((c) => c.id) } : empty);
  }, [open, employee]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addCnat = (id) => setForm((f) => (f.cnat_ids.includes(id) ? f : { ...f, cnat_ids: [...f.cnat_ids, id] }));
  const removeCnat = (id) => setForm((f) => ({ ...f, cnat_ids: f.cnat_ids.filter((x) => x !== id) }));
  const cnatName = (id) => cnats.find((c) => c.id === id)?.name || id;

  const submit = async () => {
    if (!form.full_name || !form.job_role) { toast.error("Name and job role are required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      elearning_percentage: Number(form.elearning_percentage) || 0,
      kpi_compliance: Number(form.kpi_compliance) || 0,
      supervision_count: Number(form.supervision_count) || 0,
      region_id: form.region_id || null,
      rcsm_id: form.rcsm_id || null,
    };
    try {
      if (employee) {
        await api.put(`/employees/${employee.id}`, payload);
        toast.success("Employee updated");
        onOpenChange(false);
        onSaved?.();
      } else {
        const res = await api.post("/employees", payload);
        if (res.data?.new_starter_task_id) {
          toast.success("New starter added — allocate a trainer");
          setNewStarter({ taskId: res.data.new_starter_task_id, name: form.full_name });
        } else {
          toast.success("Employee added");
          onOpenChange(false);
          onSaved?.();
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save employee");
    } finally {
      setSaving(false);
    }
  };

  const closeAll = () => { setNewStarter(null); setTrainerId(""); onOpenChange(false); onSaved?.(); };
  const allocate = async () => {
    setAllocating(true);
    try {
      await api.put(`/tasks/${newStarter.taskId}`, { assignee_id: trainerId });
      toast.success("Trainer allocated");
      closeAll();
    } catch (e) { toast.error("Could not allocate trainer"); }
    finally { setAllocating(false); }
  };

  return (
    <>
    <Dialog open={open && !newStarter} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto esp-scroll" data-testid="employee-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{employee ? "Edit employee" : "Add employee"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Full name" required>
            <Input data-testid="emp-name-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
          </Field>
          <Field label="Job role">
            <Select value={form.job_role} onValueChange={(v) => set("job_role", v)}>
              <SelectTrigger data-testid="emp-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent>{jobRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="RCSM (manager)">
            <Select value={form.rcsm_id || ""} onValueChange={(v) => set("rcsm_id", v)}>
              <SelectTrigger data-testid="emp-rcsm-select"><SelectValue placeholder="Select RCSM" /></SelectTrigger>
              <SelectContent>{rcsms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="CNAT(s)">
            <Select value="" onValueChange={addCnat}>
              <SelectTrigger data-testid="emp-cnat-select"><SelectValue placeholder="Add CNAT" /></SelectTrigger>
              <SelectContent>{cnats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            {form.cnat_ids.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {form.cnat_ids.map((id) => (
                  <span key={id} className="flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 text-xs text-orange-700">
                    {cnatName(id)}
                    <button onClick={() => removeCnat(id)}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </Field>
          <Field label="Address" full>
            <Input data-testid="emp-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} />
          </Field>
          <Field label="Postcode">
            <Input data-testid="emp-postcode-input" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} />
          </Field>
          <Field label="Hours">
            <Select value={form.hours} onValueChange={(v) => set("hours", v)}>
              <SelectTrigger data-testid="emp-hours-select"><SelectValue /></SelectTrigger>
              <SelectContent>{HOURS_OPTIONS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Work phone">
            <Input value={form.work_phone} onChange={(e) => set("work_phone", e.target.value)} />
          </Field>
          <Field label="Personal phone">
            <Input value={form.personal_phone} onChange={(e) => set("personal_phone", e.target.value)} />
          </Field>
          <Field label="Work email" full>
            <Input data-testid="emp-email-input" value={form.work_email} onChange={(e) => set("work_email", e.target.value)} />
          </Field>
          <Field label="Region">
            <Select value={form.region_id || ""} onValueChange={(v) => set("region_id", v)}>
              <SelectTrigger data-testid="emp-region-select"><SelectValue placeholder="Select region" /></SelectTrigger>
              <SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Employment status">
            <Select value={form.employment_status} onValueChange={(v) => set("employment_status", v)}>
              <SelectTrigger data-testid="emp-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="eLeaP training %">
            <Input type="number" min="0" max="100" data-testid="emp-training-input" value={form.elearning_percentage} onChange={(e) => set("elearning_percentage", e.target.value)} />
          </Field>
          <Field label="Supervisions (count)">
            <Input type="number" min="0" value={form.supervision_count} onChange={(e) => set("supervision_count", e.target.value)} />
          </Field>
          <Field label="Start date">
            <Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} />
          </Field>
          <Field label="Next review date">
            <Input type="date" value={form.next_review_date || ""} onChange={(e) => set("next_review_date", e.target.value)} />
          </Field>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="emp-save-button" onClick={submit} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
            {saving ? "Saving…" : employee ? "Save changes" : "Add employee"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={!!newStarter} onOpenChange={(v) => { if (!v) closeAll(); }}>
      <DialogContent className="max-w-md" data-testid="new-starter-allocate-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Allocate new starter training</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          A <strong>New Starter Initial Sign Off</strong> training document has been created for{" "}
          <strong>{newStarter?.name}</strong>. Allocate it to a trainer to begin.
        </p>
        <div className="mt-4">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Trainer (Clinical Training Manager or CNAT)
          </Label>
          <div className="mt-1.5">
            <Select value={trainerId} onValueChange={setTrainerId}>
              <SelectTrigger data-testid="new-starter-trainer-select"><SelectValue placeholder="Select a trainer" /></SelectTrigger>
              <SelectContent>
                {trainers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} — {roleLabel(t.role)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={closeAll}>Skip for now</Button>
          <Button data-testid="new-starter-allocate-button" onClick={allocate} disabled={!trainerId || allocating} className="bg-orange-500 hover:bg-orange-600">
            {allocating ? "Allocating…" : "Allocate trainer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

function Field({ label, required, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}{required && <span className="text-orange-500"> *</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
